import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { articles } from "@/mocks/articles";
import { movies } from "@/mocks/movies";
import NotFound from "@/pages/NotFound";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-16">
          <NotFound />
        </div>
        <Footer />
      </>
    );
  }

  const relatedMovies = movies.filter((m) => article.relatedIds.includes(m.id));

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    image: article.image,
    datePublished: article.updateTime,
    author: {
      "@type": "Organization",
      name: "红桃影视",
    },
    publisher: {
      "@type": "Organization",
      name: "红桃影视",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://hongtaomovies.com/" },
      { "@type": "ListItem", position: 2, name: article.categoryName, item: `https://hongtaomovies.com/category/${article.category}` },
      { "@type": "ListItem", position: 3, name: article.title, item: `https://hongtaomovies.com/article/${article.id}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <div className="pt-16">
        <Breadcrumb />
      </div>

      <main>
        <article>
          <section className="relative w-full h-[350px] md:h-[500px] overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 红桃影视在线观看`}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/30 to-foreground-950/40"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 max-w-4xl mx-auto">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="bg-accent-500 text-foreground-950 text-xs font-bold px-2.5 py-1 rounded-md">{article.rating}</span>
                <span className="text-background-100/80 text-sm">{article.year}</span>
                <span className="text-background-100/50">·</span>
                <span className="text-background-100/80 text-sm">{article.duration}</span>
                <span className="text-background-100/50">·</span>
                <span className="text-background-100/80 text-sm">{article.categoryName}</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-2">
                <strong>{article.title}</strong>
              </h1>
              <p className="text-sm md:text-base text-background-100/70 max-w-2xl line-clamp-2">{article.summary}</p>
            </div>
          </section>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8 p-5 rounded-xl bg-background-100/80 border border-background-200/60">
              <div>
                <span className="text-xs text-foreground-500">导演</span>
                <p className="text-sm font-medium text-foreground-900 mt-0.5">{article.director}</p>
              </div>
              <div>
                <span className="text-xs text-foreground-500">主演</span>
                <p className="text-sm font-medium text-foreground-900 mt-0.5">
                  {article.actors.slice(0, 2).join(" / ")}
                  {article.actors.length > 2 && " 等"}
                </p>
              </div>
              <div>
                <span className="text-xs text-foreground-500">类型</span>
                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-accent-100 text-accent-800 px-2 py-0.5 rounded">{tag}</span>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs text-foreground-500">更新</span>
                <p className="text-sm font-medium text-foreground-900 mt-0.5">{article.updateTime}</p>
              </div>
            </div>

            <div className="prose-custom space-y-5">
              {article.content.map((block, idx) => {
                if (block.type === "heading") {
                  return (
                    <h2 key={idx} className="text-xl font-heading font-bold text-foreground-900 mt-8 mb-3">
                      {block.text}
                    </h2>
                  );
                }
                return (
                  <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {block.text}
                  </p>
                );
              })}
            </div>

            {relatedMovies.length > 0 && (
              <section className="mt-12 pt-10 border-t border-background-200/60">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">
                  相关<strong>推荐</strong>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {relatedMovies.map((movie) => (
                    <MovieCard key={movie.id} {...movie} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}